import { Path } from "../../../../static/Constants";
import { colors } from "../../../../theme";
import Button from "../../../Components/ui/inputs/CommonButton";
import React, { useEffect } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import NavBar from "../../../Components/NavBar";
import { useAccount } from "../../../Context/AccountContext";
import WalletContent from "./components/WalletContent";

const sections = [
  { path: Path.wallet, key: "overview", label: "Overview" },
  { path: Path.walletAddBalance, key: "add-balance", label: "Add balance" },
  { path: Path.walletTransactions, key: "transactions", label: "Transactions" },
  {
    path: Path.walletApprovals,
    key: "approvals",
    label: "Approvals",
    admin: true,
  },
];

export default function WalletPage() {
  const { user, loading } = useAccount();
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, "");
  const section = sections.find((item) => item.path === pathname);
  useEffect(() => {
    const previous = document.title;
    document.title = `${section?.label || "Wallet"} | AutoBET`;
    return () => {
      document.title = previous;
    };
  }, [section]);

  if (!loading && !user) {
    return (
      <Redirect
        to={{
          pathname: Path.login,
          state: { from: location.pathname + location.search },
        }}
      />
    );
  }

  return (
    <>
      <NavBar />
      <Box
        component="main"
        sx={{
          minHeight: "80vh",
          px: { xs: 2, sm: 3 },
          pt: "110px",
          pb: 8,
          background: colors.background.subtle,
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <Typography
            component="h1"
            variant="h4"
            fontWeight={750}
            sx={{ mb: 3 }}
          >
            My wallet
          </Typography>
          {loading ? (
            <CircularProgress aria-label="Checking account" />
          ) : (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems="stretch"
            >
              <Paper
                component="nav"
                aria-label="Wallet navigation"
                elevation={0}
                sx={{
                  p: 1.5,
                  border: `1px solid ${colors.neutral.border}`,
                  borderRadius: 3,
                  width: { md: 220 },
                  flexShrink: 0,
                  alignSelf: { md: "flex-start" },
                }}
              >
                <Stack
                  direction={{ xs: "row", md: "column" }}
                  useFlexGap
                  flexWrap="wrap"
                  spacing={1}
                >
                  {sections
                    .filter((item) => !item.admin || user.role === "admin")
                    .map((item) => (
                      <Button
                        key={item.key}
                        component={Link}
                        to={item.path}
                        aria-current={section === item ? "page" : undefined}
                        variant={section === item ? "contained" : "text"}
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          color:
                            section === item
                              ? `${colors.neutral.white} !important`
                              : `${colors.primary.dark} !important`,
                          backgroundColor:
                            section === item
                              ? `${colors.primary.dark} !important`
                              : "transparent !important",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor:
                              section === item
                                ? `${colors.primary.dark} !important`
                                : `${colors.primary.lighter} !important`,
                          },
                          "&.Mui-focusVisible": {
                            outline: `2px solid ${colors.primary.dark}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                </Stack>
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: `1px solid ${colors.neutral.border}`,
                  borderRadius: 3,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {!section ? (
                  <Alert severity="info">
                    This wallet page was not found. Choose a page from the
                    wallet navigation.
                  </Alert>
                ) : section.admin && user.role !== "admin" ? (
                  <Alert severity="error">
                    Only administrators can access deposit approvals.
                  </Alert>
                ) : (
                  <WalletContent
                    key={`${user.id}:${section.key}`}
                    section={section.key}
                  />
                )}
              </Paper>
            </Stack>
          )}
        </Box>
      </Box>
    </>
  );
}
